import type { BlokkerStatus, Card, Rank, Suit } from './game'
import { POINTS_PER_BONUS_CARD } from './game'
import { PATTERNS } from './scoring'
import { BLOKKER } from './blokker'
import { PlayingCard } from './PlayingCard'

const c = (rank: Rank, suit: Suit): Card => ({ rank, suit })
const cb = (rank: Rank, suit: Suit, blokker: BlokkerStatus): Card => ({ rank, suit, blokker })

interface Entry {
  key: keyof typeof PATTERNS
  description: string
  cards: Card[]
}

const ENTRIES: Entry[] = [
  { key: 'fourOfAKind',   description: 'Four of the same rank. Positions don’t matter.',            cards: [c('king','clubs'), c('ace','diamonds'), c('king','hearts'), c('king','spades'), c('king','diamonds')] },
  { key: 'fullHouse',     description: 'Three of one rank plus two of another. Positions don’t matter.', cards: [c('king','clubs'), c('9','hearts'), c('king','spades'), c('9','diamonds'), c('king','hearts')] },
  { key: 'royalFlush',    description: '10-J-Q-K-A, all the same suit.',                             cards: [c('10','hearts'), c('jack','hearts'), c('queen','hearts'), c('king','hearts'), c('ace','hearts')] },
  { key: 'straightFlush', description: 'Five consecutive ranks, all the same suit.',                 cards: [c('5','clubs'), c('6','clubs'), c('7','clubs'), c('8','clubs'), c('9','clubs')] },
  { key: 'acesHigh',      description: '10-J-Q-K-A straight, mixed suits.',                          cards: [c('10','clubs'), c('jack','diamonds'), c('queen','hearts'), c('king','spades'), c('ace','clubs')] },
  { key: 'wheelFlush',    description: 'A-2-3-4-5, all the same suit.',                              cards: [c('ace','spades'), c('2','spades'), c('3','spades'), c('4','spades'), c('5','spades')] },
  { key: 'straight',      description: 'Five consecutive ranks in the middle, mixed suits.',         cards: [c('5','clubs'), c('6','diamonds'), c('7','hearts'), c('8','spades'), c('9','clubs')] },
  { key: 'wheel',         description: 'A-2-3-4-5 straight, mixed suits.',                           cards: [c('ace','clubs'), c('2','diamonds'), c('3','hearts'), c('4','spades'), c('5','clubs')] },
  { key: 'flush',         description: 'Five cards of the same suit, not a straight.',               cards: [c('2','spades'), c('5','spades'), c('8','spades'), c('jack','spades'), c('king','spades')] },
  { key: 'twoPair',       description: 'Two separate pairs. Each pair side by side.',                cards: [c('7','clubs'), c('7','hearts'), c('3','diamonds'), c('king','spades'), c('king','clubs')] },
  { key: 'threeOfAKind',  description: 'Three of the same rank, side by side.',                      cards: [c('9','clubs'), c('9','hearts'), c('9','spades'), c('2','diamonds'), c('king','clubs')] },
  { key: 'pair',          description: 'Two of the same rank, side by side.',                        cards: [c('7','clubs'), c('7','hearts'), c('2','diamonds'), c('jack','spades'), c('9','clubs')] },
]

export function Rules({ onBack }: { onBack: () => void }) {
  return (
    <div className="overlay" onClick={onBack}>
      <div className="menu-panel rules-panel" onClick={e => e.stopPropagation()}>
        <div className="rules-scroll">
          <h2 className="rules-title">Blokkers</h2>
          <p className="rules-intro">
            As your score climbs, cards on the grid can turn into <strong>blokkers</strong> — cards on a deadline.
            Each non-scoring move can convert one card; a move that scores a row or column skips both spawning and ticking,
            so scoring is your defence.
          </p>
          <div className="rules-entry">
            <div className="rules-entry-head">
              <span className="rules-name">Counting down</span>
            </div>
            <div className="rules-entry-desc">
              A blokker spawns with a countdown shown on the card. The border warns how close it is —
              yellow at first, then orange, then red on the last turn. While the counter is above zero
              the card still counts toward patterns; clear it as part of any match and it goes with the rest.
            </div>
            <div className="rules-entry-cards">
              <PlayingCard card={cb('5', 'clubs', { stage: 1, turns: BLOKKER.START_TURNS })} />
              <PlayingCard card={cb('7', 'diamonds', { stage: 2, turns: 2 })} />
              <PlayingCard card={cb('jack', 'hearts', { stage: 2, turns: 1 })} />
            </div>
          </div>
          <div className="rules-entry">
            <div className="rules-entry-head">
              <span className="rules-name">Locked</span>
            </div>
            <div className="rules-entry-desc">
              If the countdown hits zero the blokker locks. Locked cards no longer count toward patterns —
              rows or columns running through one cannot score. They demand {BLOKKER.STAGE_3_DEMAND} points each;
              every point you score elsewhere drains the demand. Reach zero and the card clears.
            </div>
            <div className="rules-entry-cards">
              <PlayingCard card={cb('king', 'spades', { stage: 3, remaining: BLOKKER.STAGE_3_DEMAND })} />
            </div>
          </div>
          <h2 className="rules-title">How to play</h2>
          <p className="rules-intro">
            Deal cards onto the 5×5 grid. Complete a pattern across any row or column to score — matching cards clear from the board.
            When a row-match and a column-match share a cell, both line scores double.
            Every {POINTS_PER_BONUS_CARD} points earns a bonus card.
          </p>
          <h2 className="rules-title">Patterns</h2>
          {ENTRIES.map(entry => {
            const p = PATTERNS[entry.key]
            return (
              <div key={entry.key} className="rules-entry">
                <div className="rules-entry-head">
                  <span className="rules-name">{p.name}</span>
                  <span className="rules-score">+{p.score}</span>
                </div>
                <div className="rules-entry-desc">{entry.description}</div>
                <div className="rules-entry-cards">
                  {entry.cards.map((card, i) => (
                    <PlayingCard key={i} card={card} />
                  ))}
                </div>
              </div>
            )
          })}
          <div className="rules-version">{import.meta.env.VITE_VERSION ?? 'dev'}</div>
        </div>
        <button className="menu-item" onClick={onBack}>Back</button>
      </div>
    </div>
  )
}
